import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

const GlobalCtx = createContext(null);

// Build WS URL for /api/ws/global
function globalWsUrl(token) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const u = new URL(`${BACKEND_URL}/api/ws/global`);
  const proto = u.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${u.host}${u.pathname}?token=${encodeURIComponent(token)}`;
}

export function GlobalSocketProvider({ children }) {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [broadcast, setBroadcast] = useState(null);
  const [lastGift, setLastGift] = useState(null);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const wsRef = useRef(null);
  const broadcastTimerRef = useRef(null);

  const dismissBroadcast = useCallback(() => {
    setBroadcast(null);
    if (broadcastTimerRef.current) { clearTimeout(broadcastTimerRef.current); broadcastTimerRef.current = null; }
  }, []);

  const refreshRequestCount = useCallback(async () => {
    try {
      const t = localStorage.getItem("session_token");
      if (!t) return;
      const r = await api.get("/friends/requests", { headers: { Authorization: `Bearer ${t}` } });
      setPendingRequestCount((r.data.incoming || []).length);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!user) return;
    refreshRequestCount();
  }, [user?.user_id, refreshRequestCount]);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("session_token");
    if (!token) return;

    const ws = new WebSocket(globalWsUrl(token));
    wsRef.current = ws;

    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "coin_update") {
          setUser(prev => prev ? { ...prev, coins: data.coins } : prev);
        } else if (data.type === "coin_gift") {
          setLastGift({ amount: data.amount, from: data.from, at: Date.now() });
        } else if (data.type === "coin_gift_all") {
          setLastGift({ amount: data.amount, from: data.from, at: Date.now(), all: true });
        } else if (data.type === "broadcast") {
          const id = Math.random().toString(36).slice(2);
          setBroadcast({ ...data, id });
          if (broadcastTimerRef.current) clearTimeout(broadcastTimerRef.current);
          broadcastTimerRef.current = setTimeout(() => setBroadcast(null), data.duration_ms || 6000);
        } else if (data.type === "friend_request") {
          const name = data.from?.name || "Someone";
          setPendingRequestCount(c => c + 1);
          toast(`${name} sent you a friend request`, {
            description: "Open Friends to accept or decline.",
            action: { label: "View", onClick: () => nav("/friends") },
            duration: 8000,
          });
        } else if (data.type === "friend_accepted") {
          const name = data.from?.name || "Your new friend";
          toast.success(`${name} accepted your friend request`, { duration: 6000 });
        } else if (data.type === "room_invite") {
          const name = data.from?.name || "A friend";
          const mode = data.mode_label || data.mode || "a game";
          toast(`${name} invited you to ${mode}`, {
            description: "Tap Join to sit at their table.",
            action: {
              label: "Join",
              onClick: () => nav(`/game/${data.room_id}`),
            },
            duration: 15000,
          });
        }
      } catch (e) {}
    };
    ws.onclose = () => {};
    ws.onerror = () => {};

    return () => { try { ws.close(); } catch (e) {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  return (
    <GlobalCtx.Provider value={{ broadcast, dismissBroadcast, lastGift, pendingRequestCount, refreshRequestCount }}>
      {children}
    </GlobalCtx.Provider>
  );
}

export function useGlobalSocket() {
  return useContext(GlobalCtx) || { broadcast: null, dismissBroadcast: () => {}, lastGift: null, pendingRequestCount: 0, refreshRequestCount: () => {} };
}
